import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videoArticles } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { Helmet } from '@/components/base/SEOHead';
import VideoCard from '@/components/base/VideoCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const filteredVideos = useMemo(() => {
    if (!slug) return videoArticles;
    if (slug === 'all') return videoArticles;
    if (slug === 'hot') return videoArticles.filter((v) => v.hot);
    return videoArticles.filter((v) => v.category === slug);
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground-950 mb-2">分类不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 福利视频大全`,
    description: category.description,
    url: `https://fulivideo.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '福利视频大全',
      url: 'https://fulivideo.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://fulivideo.com/article/${v.id}`,
        name: v.title,
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulivideo.com' },
      { '@type': 'ListItem', position: 2, name: category.name },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{category.name} - 福利视频大全 | 精选{category.name}资源免费看</title>
        <meta name="description" content={`${category.description}，福利视频大全为您提供最全最新的${category.name}精选视频资源，免费在线观看，每日更新。`} />
        <meta name="keywords" content={`${category.name},福利视频,${category.name}福利视频,免费${category.name},在线看${category.name},精选${category.name}合集`} />
        <link rel="canonical" href={`https://fulivideo.com/category/${category.slug}`} />
        <meta property="og:title" content={`${category.name} - 福利视频大全`} />
        <meta property="og:description" content={category.description} />
        <meta property="og:url" content={`https://fulivideo.com/category/${category.slug}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(categorySchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main>
        <section className="py-10 md:py-14 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-950 font-medium">{category.name}</span>
            </nav>

            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className={`${category.icon} text-lg`}></i>
              </span>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 text-xs text-foreground-500">
              <span>共 <strong className="text-primary-500">{filteredVideos.length}</strong> 个视频</span>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无视频，敬请期待更新</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-2">浏览其他分类</h2>
              <p className="text-sm text-foreground-500">发现更多精彩福利视频内容</p>
            </div>
            <div className="flex items-center gap-3 justify-center flex-wrap">
              {categories.filter(c => c.slug !== 'all' && c.slug !== slug).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-500 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}